import { UserRole } from 'hooks/useUserRole'

type SignupIntroCategory = 'accounting' | 'fleet' | 'safety'

interface SignupIntroConfig {
    category: SignupIntroCategory
    title: string
    description: string
    buttonLabel: string
    buttonPath: string
    videoUrl: string
}

export const CATEGORY_INTROS: Record<SignupIntroCategory, SignupIntroConfig> = {
    accounting: {
        category: 'accounting',
        title: 'Unlock Accounting',
        description: 'Connect your factoring company to start auto-submitting invoices, tracking payments, and managing driver pay all in one place.',
        buttonLabel: 'Connect Factoring',
        buttonPath: '/accounting/factoring',
        videoUrl: 'https://app.arcade.software/share/DIweYupEPtPxqnsiQVeA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    fleet: {
        category: 'fleet',
        title: 'Unlock Fleet Management',
        description: 'View truck status, manage compliance, stay on top of maintenance, and use Fuel AI to optimize your fleet.',
        buttonLabel: 'Open Fleet Overview',
        buttonPath: '/fleet/overview',
        videoUrl: 'https://app.arcade.software/share/BJEUdMGguBo2UtbWHG5i?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    safety: {
        category: 'safety',
        title: 'Unlock Safety',
        description: 'Monitor inspections, incidents, drug and alcohol, IFTA, and company files from the Safety area.',
        buttonLabel: 'Open Safety Dashboard',
        buttonPath: '/safety/dashboard',
        videoUrl: 'https://app.arcade.software/share/HWYjgu1yxnyd8dYrP557?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
}

const INTRO_ROUTE_TO_CATEGORY: Array<{ pattern: string; category: SignupIntroCategory }> = [
    { pattern: '/accounting/invoices', category: 'accounting' },
    { pattern: '/accounting/driver-pay', category: 'accounting' },
    { pattern: '/accounting/contractor-pay', category: 'accounting' },
    { pattern: '/accounting/pay-adjustments', category: 'accounting' },
    { pattern: '/accounting/factoring', category: 'accounting' },
    { pattern: '/fleet/overview', category: 'fleet' },
    { pattern: '/fleet/compliance', category: 'fleet' },
    { pattern: '/fleet/maintenance', category: 'fleet' },
    { pattern: '/fuel-ai', category: 'fleet' },
    { pattern: '/safety/dashboard', category: 'safety' },
    { pattern: '/safety/driver-compliance', category: 'safety' },
    { pattern: '/safety/dot-inspections', category: 'safety' },
    { pattern: '/safety/accidents', category: 'safety' },
    { pattern: '/safety/drug-alcohol', category: 'safety' },
    { pattern: '/safety/ifta', category: 'safety' },
    { pattern: '/safety/company-files', category: 'safety' },
]

const CATEGORY_INTRO_ENABLED_KEY = 'numeo:tmsCategoryIntroEnabled'

export function getCategorySeenKey(appId: string, userId: string, category: SignupIntroCategory): string {
    return `numeo:tmsCategoryIntroSeen:v1:${appId}:${userId}:${category}`
}

export function getCategoryDismissedKey(appId: string, userId: string): string {
    return `numeo:tmsCategoryIntroDismissed:v1:${appId}:${userId}`
}

export function getCategoryFromPathname(pathname: string): SignupIntroCategory | null {
    // Tolerate a trailing slash (e.g. `/accounting/invoices/`) so a URL variant doesn't silently
    // skip the intro AND leave the page tour un-suppressed.
    const normalized = pathname.replace(/\/+$/, '') || '/'
    const hit = INTRO_ROUTE_TO_CATEGORY.find((entry) => normalized === entry.pattern)
    return hit?.category ?? null
}

interface CategoryIntroGateArgs {
    pathname: string
    role: UserRole
    appId: string
    userId: string
}

/**
 * True when the one-time category intro modal is eligible to appear on this route
 * for this user (tmc, enabled, not dismissed, not yet seen for the category).
 *
 * The page-tour overlay calls this to stand down while the modal owns the screen,
 * so the two onboarding surfaces never stack on the same route. The page tour is
 * only deferred — its own "seen" flag stays unset, so it shows on the next visit
 * once the category intro has been handled.
 */
export function isCategoryIntroPending({ pathname, role, appId, userId }: CategoryIntroGateArgs): boolean {
    if (role !== 'tmc' || !appId || !userId) return false
    const category = getCategoryFromPathname(pathname)
    if (!category) return false
    try {
        if (localStorage.getItem(CATEGORY_INTRO_ENABLED_KEY) !== 'true') return false
        if (localStorage.getItem(getCategoryDismissedKey(appId, userId)) === 'true') return false
        if (localStorage.getItem(getCategorySeenKey(appId, userId, category)) === 'true') return false
    } catch {
        return false
    }
    return true
}
